import styled, { css } from "styled-components";

const scrollbarStyle = css`
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.gray100};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray400};
    border-radius: 4px;

    &:hover {
      background: ${({ theme }) => theme.colors.gray500};
    }
  }
`;

export const ScrollPane = styled.div`
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  padding-right: 0.5rem;
  ${scrollbarStyle};
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${({ theme }) => theme.colors.disabledDark};
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 1rem;
`;

export const SeverityAndTypeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

export const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem 2rem;
  margin-bottom: 1rem;
`;

export const MetadataItem = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: ${({ theme }) => theme.palette.secondary};
  }
`;

export const MetadataBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MetadataLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.gray600};
  margin-bottom: 0.25rem;
`;

export const MetadataValue = styled.div`
  color: ${({ theme }) => theme.palette.primary};
  font-weight: 500;
  word-break: break-word;
`;

export const SectionDivider = styled.hr`
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.elementGeneric};
  margin: 1.5rem 0;
`;

export const Section = styled.section`
  margin-bottom: 1.5rem;
`;

export const ReadOnlyText = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: ${({ theme }) => theme.palette.primary};
`;

export const PlaceholderText = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  font-style: italic;
  color: ${({ theme }) => theme.colors.disabledDark};
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 4px;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.palette.primary};
  background: ${({ theme }) => theme.colors.white};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.blue100};
  }
`;

export const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
  margin-top: 0.75rem;
`;

export const MediaTile = styled.div`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gray100};
`;

const mediaPreviewStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.gray100};
  border-radius: 4px;
`;

export const ImageThumbButton = styled.button`
  ${mediaPreviewStyle}
  padding: 0;
  margin: 0;
  border: 0;
  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid #1976d2;
    outline-offset: 2px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: opacity 150ms;
  }

  &:hover img {
    opacity: 0.9;
  }

  &:disabled {
    cursor: default;
  }
`;

export const MediaFileTile = styled.a`
  ${mediaPreviewStyle}
  flex-direction: column;
  gap: 0.75rem;
  text-decoration: none;
  cursor: pointer;
  padding: 1rem;
  transition: background-color 150ms;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray200};
  }

  &[aria-disabled="true"] {
    cursor: default;
    pointer-events: none;
  }
`;

export const FileExtBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  background-color: ${({ theme }) => theme.colors.gray300};
  color: ${({ theme }) => theme.colors.gray700};
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const FileNameLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.gray600};
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VideoThumbnail = styled.div<{ $clickable: boolean }>`
  position: relative;
  width: 100%;
  height: 200px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.gray100};
  border-radius: 4px;

  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    transition: transform 0.2s ease;
  }

  &:hover video {
    transform: ${({ $clickable }) => ($clickable ? "scale(1.03)" : "none")};
  }
`;

export const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  pointer-events: none;
`;

export const RemoveMediaButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.palette.danger};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(1.1);
  }
`;

export const HeaderRow = styled.div<{ $editing?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;

  @media (max-width: ${({ theme }) => theme.grid.breakpoints.md}) {
    ${({ $editing }) =>
      $editing &&
      `
      flex-direction: column;
      > * { width: 100%; }
    `}
  }
`;

export const HeaderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  color: ${({ theme }) => theme.colors.gray600};
  font-size: 0.9rem;
  margin-bottom: 1rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

export const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

export const FiltersToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.25rem;
`;
